import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  ArrowLeft, Search, RefreshCw, Plus, Trash2, UserPlus, Calendar, Phone, User, X, Edit, Save, 
  AlertCircle, CheckSquare, Users, ChevronDown, ChevronUp, Eye, Filter, ArrowUpDown, Plane, Bus, Bed, Home,
  DollarSign, CreditCard, Banknote, XCircle, Copy
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  'pago_total': { label: 'Pago Total', className: 'bg-green-100 text-green-800' },
  'pago_parcial': { label: 'Pago Parcial', className: 'bg-blue-100 text-blue-800' },
  'atrasado': { label: 'Pagamento Atrasado', className: 'bg-red-100 text-red-800' },
  'cancelado': { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' },
  'pendente': { label: 'Pendente de Confirmação', className: 'bg-yellow-100 text-yellow-800' }
};

const CATEGORY_LABELS = {
  'nacional-aereo': 'Nacional Aéreo',
  'nacional-rodoviario': 'Nacional Rodoviário',
  'internacional': 'Internacional',
  'passeio-1-dia': 'Passeio 1 Dia'
};

const ROOM_TYPES = [
  { id: 'compartilhado', name: 'Compartilhado', additional: 0 },
  { id: 'casal', name: 'Casal', additional: 0 },
  { id: 'individual', name: 'Individual', additional: 0 },
  { id: 'privativo', name: 'Privativo', additional: 0 }
];

// Função para obter o preço adicional do tipo de quarto do pacote selecionado
const getRoomAdditionalPrice = (packageData, roomType) => {
  if (!packageData) return 0;
  
  // Primeiro verificar room_surcharges
  if (packageData.room_surcharges && packageData.room_surcharges[roomType] !== undefined) {
    return packageData.room_surcharges[roomType] || 0;
  }
  
  // Depois verificar accommodation_options
  if (packageData.accommodation_options) {
    const option = packageData.accommodation_options.find(opt => opt.type === roomType);
    if (option && option.enabled) {
      return option.additional_price || 0;
    }
  }
  
  return 0;
};

const emptyTraveler = {
  name: '',
  cpf: '',
  rg: '',
  birthdate: '',
  gender: '',
  phone: '',
  email: '',
  is_payer: false,
  room_type: 'compartilhado'
};

const emptyInstallment = {
  value: 0,
  due_date: '',
  paid_date: '',
  expected_date: '', // Data prevista para parcelas pendentes
  payment_source: 'INTER', // 'INTER', 'STONE' ou 'SUMUP'
  notes: '',
  status: 'pendente'
};

// Pontos de embarque padrão (fallback)
const DEFAULT_BOARDING_POINTS = [
  'Rodoviária Novo Rio',
  'Casa do Alemão na W Luiz',
  'Prefeitura de Jardim Primavera'
];

// Cidades de partida padrão para aéreos (fallback)
const DEFAULT_DEPARTURE_CITIES = [
  'Rio de Janeiro',
  'São Paulo'
];

const AdminManualReservations = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Configurações de embarque (do localStorage)
  const [availableBoardingPoints, setAvailableBoardingPoints] = useState(DEFAULT_BOARDING_POINTS);
  const [availableDepartureCities, setAvailableDepartureCities] = useState(DEFAULT_DEPARTURE_CITIES);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterPackage, setFilterPackage] = useState('all');
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_cpf: '',
    customer_email: '',
    customer_phone: '',
    customer_gender: '',
    customer_birthdate: '',
    customer_rg: '',
    package_id: '',
    date_id: '',
    boarding_point: '', // Local de embarque
    quantity: 1,
    total_amount: 0,
    discount_percentage: 0, // % de desconto
    discount_amount: 0, // Valor do desconto calculado
    amount_paid: 0,
    payment_method: 'externo',
    status: 'pago_parcial',
    notes: '',
    travelers: [{ ...emptyTraveler, is_payer: true }],
    installments: [] // Array de parcelas
  });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resResponse, pkgResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/manual-reservations/`),
        axios.get(`${BACKEND_URL}/api/packages/`)
      ]);
      setReservations(resResponse.data);
      setPackages(pkgResponse.data);
      
      // Carregar configurações de embarque do localStorage
      const savedSettings = localStorage.getItem('adminSettings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          if (parsed.boardingPoints && parsed.boardingPoints.length > 0) {
            setAvailableBoardingPoints(parsed.boardingPoints);
          }
          if (parsed.departureCities && parsed.departureCities.length > 0) {
            setAvailableDepartureCities(parsed.departureCities);
          }
        } catch (e) {
          console.error('Erro ao carregar configurações:', e);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
  };

  // Buscar cliente por CPF em reservas existentes
  const searchCustomerByCPF = (cpf, travelerIndex = 0) => {
    if (!cpf || cpf.length < 14) return; // CPF formatado tem 14 caracteres
    
    // Buscar nas reservas existentes
    let foundCustomer = null;
    
    // Primeiro buscar nos travelers das reservas
    for (const r of reservations) {
      // Buscar no CPF do cliente principal
      if (r.customer_cpf === cpf) {
        foundCustomer = {
          name: r.customer_name,
          email: r.customer_email,
          phone: r.customer_phone,
          gender: r.customer_gender,
          birthdate: r.customer_birthdate,
          rg: r.customer_rg
        };
        break;
      }
      
      // Buscar nos travelers
      const traveler = (r.travelers || []).find(t => t.cpf === cpf);
      if (traveler) {
        foundCustomer = {
          name: traveler.name,
          email: traveler.email,
          phone: traveler.phone,
          gender: traveler.gender,
          birthdate: traveler.birthdate,
          rg: traveler.rg
        };
        break;
      }
    }
    
    if (foundCustomer) {
      // Atualizar o viajante específico
      setFormData(prev => {
        const newTravelers = [...prev.travelers];
        newTravelers[travelerIndex] = {
          ...newTravelers[travelerIndex],
          name: foundCustomer.name || newTravelers[travelerIndex].name,
          email: foundCustomer.email || newTravelers[travelerIndex].email,
          phone: foundCustomer.phone || newTravelers[travelerIndex].phone,
          gender: foundCustomer.gender || newTravelers[travelerIndex].gender,
          birthdate: foundCustomer.birthdate || newTravelers[travelerIndex].birthdate,
          rg: foundCustomer.rg || newTravelers[travelerIndex].rg
        };
        return { ...prev, travelers: newTravelers };
      });
      
      setMessage({ type: 'success', text: `Cliente encontrado: ${foundCustomer.name}` });
    }
  };

  // Duplicar reserva
  const duplicateReservation = (reservation) => {
    // Copiar dados da reserva mas limpar IDs e informações específicas
    const duplicatedTravelers = (reservation.travelers || []).map(t => ({
      ...t,
      id: undefined
    }));
    
    const duplicatedInstallments = (reservation.installments || []).map((inst, idx) => ({
      ...inst,
      id: `inst-${idx + 1}`,
      paid: false,
      paid_date: null
    }));
    
    setFormData({
      customer_name: reservation.customer_name || '',
      customer_cpf: reservation.customer_cpf || '',
      customer_email: reservation.customer_email || '',
      customer_phone: reservation.customer_phone || '',
      customer_gender: reservation.customer_gender || '',
      customer_birthdate: reservation.customer_birthdate || '',
      customer_rg: reservation.customer_rg || '',
      package_id: reservation.package_id || '',
      date_id: reservation.date_id || '',
      boarding_point: reservation.boarding_point || '',
      quantity: reservation.quantity || 1,
      total_amount: reservation.total_amount || 0,
      discount_percentage: reservation.discount_percentage || 0,
      discount_amount: reservation.discount_amount || 0,
      amount_paid: 0, // Zerar valor pago
      payment_method: reservation.payment_method || 'externo',
      status: 'pendente', // Status inicial
      notes: `Duplicado de reserva anterior`,
      travelers: duplicatedTravelers.length > 0 ? duplicatedTravelers : [{ ...emptyTraveler, is_payer: true }],
      installments: duplicatedInstallments
    });
    
    // Selecionar o pacote (selectedPackage é derivado de formData.package_id, já setado acima)
    
    setEditingReservation(null); // Nova reserva
    setShowModal(true);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  const getStatusBadge = (status) => {
    const c = STATUS_CONFIG[status] || { label: status || '-', className: 'bg-gray-100 text-gray-800' };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = { 'externo': 'Externo', 'pix': 'PIX', 'boleto': 'Boleto', 'cartao': 'Cartão', 'dinheiro': 'Dinheiro', 'transferencia': 'Transferência' };
    return labels[method] || method || '-';
  };

  const getRoomTypeLabel = (type) => {
    const room = ROOM_TYPES.find(r => r.id === type);
    return room?.name || type || '-';
  };

  // Calcular status automático para uma reserva (usado na tabela)
  const getAutoStatusForReservation = (res) => {
    if (res.status === 'cancelado') return { status: 'cancelado', label: '❌ Cancelado', color: 'bg-gray-200 text-gray-700' };
    
    const installments = res.installments || [];
    if (installments.length === 0) {
      // Sem parcelas - usa status original
      return STATUS_CONFIG[res.status] ? { status: res.status, ...STATUS_CONFIG[res.status] } : { status: 'pago_parcial', label: '🟡 Pago Parcial', color: 'bg-yellow-100 text-yellow-800' };
    }
    
    const today = new Date().toISOString().split('T')[0];
    const totalValue = installments.reduce((s, i) => s + (parseFloat(i.value) || 0), 0);
    const paidValue = installments.filter(i => i.status === 'pago').reduce((s, i) => s + (parseFloat(i.value) || 0), 0);
    
    // Todas pagas
    const allPaid = installments.every(i => i.status === 'pago');
    if (allPaid && paidValue >= totalValue * 0.99) {
      return { status: 'pago_total', label: '✅ Pago Total', color: 'bg-green-100 text-green-800' };
    }
    
    // Parcelas vencidas não pagas
    const hasOverdue = installments.some(i => {
      if (i.status === 'pago') return false;
      const dueDate = i.expected_date || i.due_date;
      return dueDate && dueDate < today;
    });
    
    if (hasOverdue) {
      return { status: 'atrasado', label: '🔴 Atrasado', color: 'bg-red-100 text-red-800' };
    }
    
    return { status: 'pago_parcial', label: '🟡 Pago Parcial', color: 'bg-yellow-100 text-yellow-800' };
  };

  // Get unique values for filters
  const uniquePackages = useMemo(() => {
    const pkgs = [...new Set(reservations.map(r => r.package_name).filter(Boolean))];
    return pkgs.sort();
  }, [reservations]);

  const uniqueCategories = useMemo(() => {
    const categories = [...new Set(reservations.map(r => r.package_category).filter(Boolean))];
    return categories;
  }, [reservations]);

  // Filter and sort reservations
  const filteredAndSortedReservations = useMemo(() => {
    let filtered = reservations.filter(res => {
      const matchesSearch = !searchTerm || 
        res.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.package_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.customer_cpf?.includes(searchTerm);
      
      const matchesCategory = filterCategory === 'all' || res.package_category === filterCategory;
      const matchesStatus = filterStatus === 'all' || res.status === filterStatus;
      const matchesPayment = filterPayment === 'all' || res.payment_method === filterPayment;
      const matchesPackage = filterPackage === 'all' || res.package_name === filterPackage;
      
      return matchesSearch && matchesCategory && matchesStatus && matchesPayment && matchesPackage;
    });
    
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'total_amount' || sortField === 'quantity' || sortField === 'amount_paid') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    return filtered;
  }, [reservations, searchTerm, filterCategory, filterStatus, filterPayment, filterPackage, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAndSortedReservations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAndSortedReservations.map(r => r.id));
    }
  };

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterStatus('all');
    setFilterPayment('all');
    setFilterPackage('all');
    setSearchTerm('');
  };

  const selectedPackage = packages.find(p => p.id === formData.package_id);
  const isPasseio1Dia = selectedPackage?.category === 'passeio-1-dia';

  // Calcula total baseado nas acomodações individuais
  const calculateTotal = () => {
    if (!selectedPackage) return 0;
    const basePrice = selectedPackage.price || 0;
    
    let subtotal = 0;
    if (isPasseio1Dia) {
      subtotal = basePrice * formData.travelers.length;
    } else {
      formData.travelers.forEach(t => {
        const roomExtra = getRoomAdditionalPrice(selectedPackage, t.room_type);
        subtotal += basePrice + roomExtra;
      });
    }
    
    // Aplicar desconto se houver
    const discountPct = parseFloat(formData.discount_percentage) || 0;
    const discountValue = subtotal * (discountPct / 100);
    
    return subtotal - discountValue;
  };

  // Calcula valor do desconto
  const calculateDiscount = () => {
    if (!selectedPackage) return 0;
    const basePrice = selectedPackage.price || 0;
    
    let subtotal = 0;
    if (isPasseio1Dia) {
      subtotal = basePrice * formData.travelers.length;
    } else {
      formData.travelers.forEach(t => {
        const roomExtra = getRoomAdditionalPrice(selectedPackage, t.room_type);
        subtotal += basePrice + roomExtra;
      });
    }
    
    const discountPct = parseFloat(formData.discount_percentage) || 0;
    return subtotal * (discountPct / 100);
  };

  // Subtotal sem desconto
  const calculateSubtotal = () => {
    if (!selectedPackage) return 0;
    const basePrice = selectedPackage.price || 0;
    
    let subtotal = 0;
    if (isPasseio1Dia) {
      subtotal = basePrice * formData.travelers.length;
    } else {
      formData.travelers.forEach(t => {
        const roomExtra = getRoomAdditionalPrice(selectedPackage, t.room_type);
        subtotal += basePrice + roomExtra;
      });
    }
    return subtotal;
  };

  // Calcula total pago das parcelas
  const calculatePaidFromInstallments = () => {
    return formData.installments
      .filter(inst => inst.status === 'pago')
      .reduce((sum, inst) => sum + (parseFloat(inst.value) || 0), 0);
  };

  // Calcula status automaticamente baseado nas parcelas
  const calculateAutoStatus = () => {
    if (formData.status === 'cancelado') return '❌ Cancelado';
    
    const installments = formData.installments;
    if (installments.length === 0) return '🟡 Pago Parcial';
    
    const today = new Date().toISOString().split('T')[0];
    const totalValue = installments.reduce((s, i) => s + (parseFloat(i.value) || 0), 0);
    const paidValue = installments.filter(i => i.status === 'pago').reduce((s, i) => s + (parseFloat(i.value) || 0), 0);
    
    // Verifica se todas as parcelas estão pagas
    const allPaid = installments.every(i => i.status === 'pago');
    if (allPaid && paidValue >= totalValue * 0.99) return '✅ Pago Total';
    
    // Verifica se há parcelas pendentes vencidas
    const hasOverdue = installments.some(i => {
      if (i.status === 'pago') return false;
      const dueDate = i.expected_date || i.due_date;
      return dueDate && dueDate < today;
    });
    
    if (hasOverdue) return '🔴 Atrasado';
    
    if (paidValue > 0) return '🟡 Pago Parcial';
    
    return '🟡 Pago Parcial';
  };

  const handlePackageChange = (packageId) => {
    const pkg = packages.find(p => p.id === packageId);
    setFormData(prev => {
      const newTravelers = prev.travelers.map(t => ({ ...t, room_type: 'compartilhado' }));
      return {
        ...prev,
        package_id: packageId,
        date_id: '',
        travelers: newTravelers,
        total_amount: pkg ? pkg.price * newTravelers.length : 0
      };
    });
  };

  const handleQuantityChange = (qty) => {
    const newTravelers = [...formData.travelers];
    
    while (newTravelers.length < qty) {
      newTravelers.push({ ...emptyTraveler });
    }
    while (newTravelers.length > qty) {
      newTravelers.pop();
    }
    
    if (newTravelers.length > 0) {
      newTravelers[0].is_payer = true;
    }
    
    setFormData(prev => ({
      ...prev,
      quantity: qty,
      travelers: newTravelers
    }));
  };

  // Atualiza o total quando os viajantes ou desconto mudam
  useEffect(() => {
    if (selectedPackage) {
      setFormData(prev => ({
        ...prev,
        total_amount: calculateTotal(),
        discount_amount: calculateDiscount()
      }));
    }
  }, [formData.travelers, formData.package_id, formData.discount_percentage]);

  // Atualiza amount_paid quando parcelas mudam
  useEffect(() => {
    const paidAmount = calculatePaidFromInstallments();
    setFormData(prev => ({
      ...prev,
      amount_paid: paidAmount
    }));
  }, [formData.installments]);

  const updateTraveler = (index, field, value) => {
    const newTravelers = [...formData.travelers];
    newTravelers[index] = { ...newTravelers[index], [field]: value };
    
    if (index === 0) {
      const fieldMap = {
        'name': 'customer_name',
        'cpf': 'customer_cpf',
        'email': 'customer_email',
        'phone': 'customer_phone',
        'gender': 'customer_gender',
        'birthdate': 'customer_birthdate',
        'rg': 'customer_rg'
      };
      if (fieldMap[field]) {
        setFormData(prev => ({
          ...prev,
          [fieldMap[field]]: value,
          travelers: newTravelers
        }));
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, travelers: newTravelers }));
  };

  // Installment handlers
  const addInstallment = () => {
    if (formData.installments.length >= 12) {
      setMessage({ type: 'warning', text: 'Máximo de 12 parcelas permitido!' });
      return;
    }
    setFormData(prev => ({
      ...prev,
      installments: [...prev.installments, { ...emptyInstallment }]
    }));
  };

  // Replicar parcela em Nx (mesmo dia do mês nos meses seguintes)
  const replicateInstallment = (index, numInstallments = 12) => {
    const sourceInstallment = formData.installments[index];
    if (!sourceInstallment.due_date && !sourceInstallment.expected_date) {
      setMessage({ type: 'warning', text: 'Defina uma data de vencimento ou prevista antes de replicar!' });
      return;
    }

    const remainingSlots = 12 - formData.installments.length;
    if (remainingSlots <= 0) {
      setMessage({ type: 'warning', text: 'Limite de 12 parcelas atingido!' });
      return;
    }

    // Quantas cópias fazer (numInstallments - 1 porque a original já existe)
    const copiesToMake = Math.min(numInstallments - 1, remainingSlots);
    const baseDate = new Date(sourceInstallment.due_date || sourceInstallment.expected_date);
    const dayOfMonth = baseDate.getDate();
    
    const newInstallments = [];
    for (let i = 1; i <= copiesToMake; i++) {
      // Calcular o mês seguinte
      const nextDate = new Date(baseDate);
      nextDate.setMonth(nextDate.getMonth() + i);
      
      // Ajustar para o mesmo dia do mês (lidando com meses com menos dias)
      const lastDayOfMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
      nextDate.setDate(Math.min(dayOfMonth, lastDayOfMonth));
      
      const dateStr = nextDate.toISOString().split('T')[0];
      
      newInstallments.push({
        ...emptyInstallment,
        value: sourceInstallment.value,
        due_date: dateStr,
        expected_date: dateStr,
        payment_source: sourceInstallment.payment_source || 'INTER',
        notes: `Parcela ${formData.installments.length + i} de ${numInstallments}`,
        status: 'pendente'
      });
    }

    setFormData(prev => ({
      ...prev,
      installments: [...prev.installments, ...newInstallments]
    }));

    setMessage({ type: 'success', text: `${copiesToMake} parcela(s) criada(s) com sucesso!` });
  };

  const removeInstallment = (index) => {
    setFormData(prev => ({
      ...prev,
      installments: prev.installments.filter((_, i) => i !== index)
    }));
  };

  const updateInstallment = (index, field, value) => {
    const newInstallments = [...formData.installments];
    newInstallments[index] = { ...newInstallments[index], [field]: value };
    setFormData(prev => ({ ...prev, installments: newInstallments }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Determinar status baseado nas parcelas
      let status = formData.status;
      if (formData.installments.length > 0) {
        const totalPaid = calculatePaidFromInstallments();
        const total = calculateTotal();
        if (totalPaid >= total) {
          status = 'pago_total';
        } else if (totalPaid > 0) {
          status = 'pago_parcial';
        }
      }

      const payload = {
        ...formData,
        status,
        total_amount: calculateTotal(),
        amount_paid: calculatePaidFromInstallments(),
        travelers: formData.travelers.map((t, idx) => ({
          ...t,
          is_payer: idx === 0
        }))
      };
      
      if (editingReservation) {
        await axios.put(`${BACKEND_URL}/api/manual-reservations/${editingReservation.id}`, payload);
        setMessage({ type: 'success', text: 'Reserva atualizada com sucesso!' });
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/manual-reservations/`, payload);
        if (response.data.warning) {
          setMessage({ type: 'warning', text: response.data.warning });
        } else {
          setMessage({ type: 'success', text: 'Reserva criada com sucesso!' });
        }
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao salvar reserva' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta reserva?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/manual-reservations/${id}`);
      setMessage({ type: 'success', text: 'Reserva excluída com sucesso!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao excluir reserva' });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Tem certeza que deseja excluir ${selectedIds.length} reserva(s)?`)) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/manual-reservations/bulk-delete`, { ids: selectedIds });
      setMessage({ type: 'success', text: `${selectedIds.length} reserva(s) excluída(s)!` });
      setSelectedIds([]);
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao excluir reservas' });
    }
  };

  const handleEdit = (reservation) => {
    setEditingReservation(reservation);
    
    let travelers = reservation.travelers || [];
    if (travelers.length === 0) {
      travelers = [{
        name: reservation.customer_name || '',
        cpf: reservation.customer_cpf || '',
        rg: reservation.customer_rg || '',
        birthdate: reservation.customer_birthdate || '',
        gender: reservation.customer_gender || '',
        phone: reservation.customer_phone || '',
        email: reservation.customer_email || '',
        is_payer: true,
        room_type: reservation.room_type || 'compartilhado'
      }];
    }
    
    const qty = reservation.quantity || reservation.number_of_people || 1;
    while (travelers.length < qty) {
      travelers.push({ ...emptyTraveler });
    }
    
    setFormData({
      customer_name: reservation.customer_name || '',
      customer_cpf: reservation.customer_cpf || '',
      customer_email: reservation.customer_email || '',
      customer_phone: reservation.customer_phone || '',
      customer_gender: reservation.customer_gender || '',
      customer_birthdate: reservation.customer_birthdate || '',
      customer_rg: reservation.customer_rg || '',
      package_id: reservation.package_id || '',
      date_id: reservation.date_id || '',
      boarding_point: reservation.boarding_point || '',
      quantity: qty,
      total_amount: reservation.total_amount || 0,
      discount_percentage: reservation.discount_percentage || 0,
      discount_amount: reservation.discount_amount || 0,
      amount_paid: reservation.amount_paid || 0,
      payment_method: reservation.payment_method || 'externo',
      status: reservation.status || 'pendente',
      notes: reservation.notes || '',
      travelers: travelers,
      installments: reservation.installments || []
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingReservation(null);
    setFormData({
      customer_name: '',
      customer_cpf: '',
      customer_email: '',
      customer_phone: '',
      customer_gender: '',
      customer_birthdate: '',
      customer_rg: '',
      package_id: '',
      date_id: '',
      boarding_point: '',
      quantity: 1,
      total_amount: 0,
      discount_percentage: 0,
      discount_amount: 0,
      amount_paid: 0,
      payment_method: 'externo',
      status: 'pago_parcial',
      notes: '',
      travelers: [{ ...emptyTraveler, is_payer: true }],
      installments: []
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/admin')} className="flex items-center space-x-2 hover:opacity-80">
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <div className="h-6 w-px bg-white/30"></div>
            <div className="flex items-center space-x-2">
              <UserPlus className="w-6 h-6" />
              <h1 className="text-xl font-bold">Reservas Manuais</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadData} variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-[#003366]">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
              <Plus className="w-4 h-4 mr-2" />
              Nova Reserva
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Message */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-700' : message.type === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="ml-4 font-bold">×</button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nome, CPF, email ou pacote..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-2" />
              Filtros
            </Button>
            {(filterCategory !== 'all' || filterStatus !== 'all' || filterPayment !== 'all' || filterPackage !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar filtros
              </Button>
            )}
          </div>
          
          {/* Filter Panel */}
          {showFilters && (
            <Card>
              <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">Categoria</Label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {uniqueCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Pagamento</Label>
                  <Select value={filterPayment} onValueChange={setFilterPayment}>
                    <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="externo">Externo</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="boleto">Boleto</SelectItem>
                      <SelectItem value="cartao">Cartão</SelectItem>
                      <SelectItem value="dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="transferencia">Transferência</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Pacote</Label>
                  <Select value={filterPackage} onValueChange={setFilterPackage}>
                    <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {uniquePackages.map(pkg => (
                        <SelectItem key={pkg} value={pkg}>{pkg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Actions Bar */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {selectedIds.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir {selectedIds.length} selecionada(s)
              </Button>
            )}
          </div>
          <span className="text-sm text-gray-600">
            <strong>{filteredAndSortedReservations.length}</strong> reserva(s) | 
            <strong className="text-green-600 ml-2">{filteredAndSortedReservations.filter(r => r.status === 'pago_total').length}</strong> paga(s)
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#003366]"></div>
          </div>
        ) : filteredAndSortedReservations.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-500">Nenhuma reserva manual encontrada</CardContent></Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left w-10">
                      <Checkbox
                        checked={selectedIds.length === filteredAndSortedReservations.length && filteredAndSortedReservations.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('customer_name')}>
                      <div className="flex items-center gap-1">Cliente <SortIcon field="customer_name" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('package_name')}>
                      <div className="flex items-center gap-1">Pacote <SortIcon field="package_name" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('selected_date')}>
                      <div className="flex items-center gap-1">Data <SortIcon field="selected_date" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('quantity')}>
                      <div className="flex items-center gap-1">Qtd <SortIcon field="quantity" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('total_amount')}>
                      <div className="flex items-center gap-1">Valor <SortIcon field="total_amount" /></div>
                    </th>
                    <th className="p-3 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">Status <SortIcon field="status" /></div>
                    </th>
                    <th className="p-3 text-left">Parcelas</th>
                    <th className="p-3 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedReservations.map((res) => (
                    <tr key={res.id} className={`border-b hover:bg-gray-50 ${selectedIds.includes(res.id) ? 'bg-blue-50' : ''}`}>
                      <td className="p-3">
                        <Checkbox
                          checked={selectedIds.includes(res.id)}
                          onCheckedChange={() => toggleSelect(res.id)}
                        />
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{res.customer_name}</div>
                        <div className="text-xs text-gray-500">{res.customer_cpf || '-'}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {(res.package_category?.toLowerCase().includes('aereo') || res.package_category?.toLowerCase().includes('aéreo') || res.package_category === 'internacional') ? (
                            <Plane className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Bus className="w-4 h-4 text-green-600" />
                          )}
                          <span>{res.package_name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-gray-600">{res.selected_date || '-'}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-gray-400" />
                          {res.quantity || res.number_of_people || 1}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-[#003366]">{formatPrice(res.total_amount)}</div>
                        {res.amount_paid > 0 && res.amount_paid < res.total_amount && (
                          <div className="text-xs text-green-600">Pago: {formatPrice(res.amount_paid)}</div>
                        )}
                      </td>
                      <td className="p-3">
                        {(() => {
                          const autoStatus = getAutoStatusForReservation(res);
                          return (
                            <Badge className={autoStatus.color}>
                              {autoStatus.label}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="p-3">
                        {res.installments?.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Banknote className="w-4 h-4 text-green-600" />
                            <span className="text-xs">{res.installments.filter(i => i.status === 'pago').length}/{res.installments.length}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setSelectedReservation(res)} title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(res)} title="Editar">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => duplicateReservation(res)} title="Duplicar reserva">
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(res.id)} title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Reserva Manual</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs text-gray-500">Nome</label><p className="font-medium">{selectedReservation.customer_name}</p></div>
                <div><label className="text-xs text-gray-500">CPF</label><p>{selectedReservation.customer_cpf || '-'}</p></div>
                <div><label className="text-xs text-gray-500">Pacote</label><p className="font-medium">{selectedReservation.package_name}</p></div>
                <div><label className="text-xs text-gray-500">Data</label><p>{selectedReservation.selected_date || '-'}</p></div>
                <div><label className="text-xs text-gray-500">Valor Total</label><p className="font-medium text-[#003366]">{formatPrice(selectedReservation.total_amount)}</p></div>
                <div><label className="text-xs text-gray-500">Valor Pago</label><p className="text-green-600">{formatPrice(selectedReservation.amount_paid || 0)}</p></div>
                <div><label className="text-xs text-gray-500">Status</label>{getStatusBadge(selectedReservation.status)}</div>
              </div>
              
              {/* Parcelas */}
              {selectedReservation.installments && selectedReservation.installments.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Banknote className="w-4 h-4" />
                    Parcelas ({selectedReservation.installments.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedReservation.installments.map((inst, idx) => (
                      <div key={idx} className={`p-2 rounded flex justify-between items-center ${inst.status === 'pago' ? 'bg-green-50' : 'bg-gray-50'}`}>
                        <div>
                          <span className="font-medium">Parcela {idx + 1}</span>
                          <span className="mx-2 text-gray-400">•</span>
                          <span className={inst.status === 'pago' ? 'text-green-600' : 'text-gray-600'}>{formatPrice(inst.value)}</span>
                          {inst.due_date && <span className="text-xs text-gray-500 ml-2">Venc: {inst.due_date}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {inst.payment_source && <span className="text-xs text-gray-500">{inst.payment_source}</span>}
                          <Badge className={inst.status === 'pago' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {inst.status === 'pago' ? 'Pago' : 'Pendente'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Travelers */}
              {selectedReservation.travelers && selectedReservation.travelers.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Viajantes ({selectedReservation.travelers.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedReservation.travelers.map((t, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded flex justify-between items-center">
                        <div>
                          <span className="font-medium">{t.name || 'Sem nome'}</span>
                          {t.is_payer && <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Pagador</Badge>}
                          <span className="ml-2 text-xs text-gray-500">({getRoomTypeLabel(t.room_type)})</span>
                        </div>
                        <span className="text-xs text-gray-500">{t.cpf || '-'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReservation ? 'Editar Reserva Manual' : 'Nova Reserva Manual'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Package Selection */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Pacote *</Label>
                <Select value={formData.package_id} onValueChange={handlePackageChange} required>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {packages.map(pkg => (
                      <SelectItem key={pkg.id} value={pkg.id}>{pkg.name} - {formatPrice(pkg.price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data *</Label>
                <Select value={formData.date_id} onValueChange={(v) => setFormData(prev => ({ ...prev, date_id: v }))} disabled={!selectedPackage} required>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {selectedPackage?.dates?.map(date => (
                      <SelectItem key={date.id} value={date.id}>{date.date} ({date.spots_available} vagas)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantidade de Pessoas</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={formData.quantity}
                  onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="is_cancelled"
                  checked={formData.status === 'cancelado'}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    status: e.target.checked ? 'cancelado' : 'pago_parcial',
                    cancellation_date: e.target.checked ? new Date().toISOString().split('T')[0] : ''
                  }))}
                  className="w-5 h-5 text-red-600 rounded"
                />
                <Label htmlFor="is_cancelled" className="text-red-600 font-medium cursor-pointer">
                  ❌ Cancelado
                </Label>
              </div>
            </div>
            
            {/* Bloco de Cancelamento (aparece quando marcado) */}
            {formData.status === 'cancelado' && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-4">
                <h4 className="font-semibold text-red-700 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Dados do Cancelamento
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-red-700">Data Cancelamento</Label>
                    <Input
                      type="date"
                      value={formData.cancellation_date || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, cancellation_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-red-700">Valor Reembolso</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.refund_amount || 0}
                      onChange={(e) => setFormData(prev => ({ ...prev, refund_amount: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label className="text-red-700">Forma Reembolso</Label>
                    <Select 
                      value={formData.refund_method || 'cartao'} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, refund_method: v }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cartao">💳 Estorno Cartão</SelectItem>
                        <SelectItem value="pix_inter">🏦 PIX/INTER</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Travelers with individual accommodation */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Viajantes ({formData.travelers.length})
                {!isPasseio1Dia && <span className="text-sm font-normal text-gray-500 ml-2">(selecione a acomodação de cada pessoa)</span>}
              </h3>
              
              {formData.travelers.map((traveler, idx) => (
                <div key={idx} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-sm">
                      {idx === 0 ? '👤 Responsável pelo Pagamento' : `Acompanhante ${idx}`}
                    </h4>
                    <div className="flex items-center gap-2">
                      {idx === 0 && <Badge className="bg-green-100 text-green-700">Pagador</Badge>}
                      {!isPasseio1Dia && (
                        <Select value={traveler.room_type || 'compartilhado'} onValueChange={(v) => updateTraveler(idx, 'room_type', v)}>
                          <SelectTrigger className="w-[180px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROOM_TYPES.map(room => (
                              <SelectItem key={room.id} value={room.id}>
                                <div className="flex items-center gap-2">
                                  {room.id === 'compartilhado' && <Users className="w-4 h-4" />}
                                  {room.id === 'casal' && <Bed className="w-4 h-4" />}
                                  {room.id === 'individual' && <User className="w-4 h-4" />}
                                  {room.id === 'privativo' && <Home className="w-4 h-4" />}
                                  {room.name}
                                  {(() => {
                                    const additionalPrice = getRoomAdditionalPrice(selectedPackage, room.id);
                                    return additionalPrice > 0 && <span className="text-xs text-gray-500">(+{formatPrice(additionalPrice)})</span>;
                                  })()}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Nome Completo *</Label>
                      <Input
                        required={idx === 0}
                        value={traveler.name}
                        onChange={(e) => updateTraveler(idx, 'name', e.target.value)}
                        placeholder="Nome completo"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">CPF {idx === 0 && '(busca automática)'}</Label>
                      <Input
                        value={traveler.cpf}
                        onChange={(e) => updateTraveler(idx, 'cpf', formatCPF(e.target.value))}
                        onBlur={(e) => {
                          if (idx === 0 && e.target.value.length === 14) {
                            searchCustomerByCPF(e.target.value, idx);
                          }
                        }}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">RG</Label>
                      <Input
                        value={traveler.rg}
                        onChange={(e) => updateTraveler(idx, 'rg', e.target.value)}
                        placeholder="00.000.000-0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data de Nascimento</Label>
                      <Input
                        value={traveler.birthdate}
                        onChange={(e) => updateTraveler(idx, 'birthdate', e.target.value)}
                        placeholder="DD/MM/AAAA"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Sexo</Label>
                      <Select value={traveler.gender} onValueChange={(v) => updateTraveler(idx, 'gender', v)}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="masculino">Masculino</SelectItem>
                          <SelectItem value="feminino">Feminino</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {idx === 0 && (
                      <>
                        <div>
                          <Label className="text-xs">Telefone</Label>
                          <Input
                            value={traveler.phone}
                            onChange={(e) => updateTraveler(idx, 'phone', formatPhone(e.target.value))}
                            placeholder="(00) 00000-0000"
                            maxLength={15}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs">E-mail</Label>
                          <Input
                            type="email"
                            value={traveler.email}
                            onChange={(e) => updateTraveler(idx, 'email', e.target.value)}
                            placeholder="email@exemplo.com"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Parcelas */}
            <div className="space-y-4 p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-green-600" />
                  Parcelas de Pagamento ({formData.installments.length}/12)
                </h3>
                <Button 
                  type="button" 
                  size="sm" 
                  onClick={addInstallment}
                  disabled={formData.installments.length >= 12}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Parcela
                </Button>
              </div>
              
              {formData.installments.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma parcela adicionada. Clique em &quot;Adicionar Parcela&quot; para registrar os pagamentos.</p>
              ) : (
                <div className="space-y-3">
                  {formData.installments.map((inst, idx) => (
                    <div key={idx} className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">Parcela {idx + 1}</span>
                        <div className="flex items-center gap-1">
                          {formData.installments.length < 12 && (inst.due_date || inst.expected_date) && (
                            <Select 
                              onValueChange={(v) => replicateInstallment(idx, parseInt(v))}
                            >
                              <SelectTrigger className="h-7 w-20 text-xs text-blue-600 border-blue-300">
                                <SelectValue placeholder="Replicar" />
                              </SelectTrigger>
                              <SelectContent>
                                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter(n => formData.installments.length + n - 1 <= 12).map(n => (
                                  <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <Button type="button" size="sm" variant="ghost" className="text-red-600 h-6 w-6 p-0" onClick={() => removeInstallment(idx)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <div>
                          <Label className="text-xs">Valor (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={inst.value}
                            onChange={(e) => updateInstallment(idx, 'value', parseFloat(e.target.value) || 0)}
                            placeholder="0,00"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Vencimento</Label>
                          <Input
                            type="date"
                            value={inst.due_date}
                            onChange={(e) => updateInstallment(idx, 'due_date', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Banco/Fonte *</Label>
                          <Select value={inst.payment_source || 'INTER'} onValueChange={(v) => updateInstallment(idx, 'payment_source', v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="INTER">🏦 INTER</SelectItem>
                              <SelectItem value="STONE">💳 STONE</SelectItem>
                              <SelectItem value="SUMUP">💳 SUMUP</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Status</Label>
                          <Select value={inst.status} onValueChange={(v) => updateInstallment(idx, 'status', v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pendente">⏳ Pendente</SelectItem>
                              <SelectItem value="pago">✅ Pago</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {inst.status === 'pago' && (
                          <div>
                            <Label className="text-xs">Data Pgto</Label>
                            <Input
                              type="date"
                              value={inst.paid_date}
                              onChange={(e) => updateInstallment(idx, 'paid_date', e.target.value)}
                            />
                          </div>
                        )}
                        {inst.status === 'pendente' && (
                          <div>
                            <Label className="text-xs">Data Prevista</Label>
                            <Input
                              type="date"
                              value={inst.expected_date || inst.due_date}
                              onChange={(e) => updateInstallment(idx, 'expected_date', e.target.value)}
                            />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Observação</Label>
                          <Input
                            value={inst.notes}
                            onChange={(e) => updateInstallment(idx, 'notes', e.target.value)}
                            placeholder="Obs..."
                          />
                        </div>
                      </div>
                      {inst.payment_source === 'STONE' && (
                        <div className="mt-2 text-xs text-cyan-600">
                          💳 STONE: Valores de cartão importados via CSV
                        </div>
                      )}
                      {inst.payment_source === 'SUMUP' && (
                        <div className="mt-2 text-xs text-pink-600">
                          💳 SUMUP: Valores de cartão contabilizados no fluxo
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Resumo das parcelas */}
                  <div className="p-3 bg-green-100 rounded-lg flex justify-between items-center">
                    <span className="font-medium">Total das Parcelas:</span>
                    <div className="text-right">
                      <span className="font-bold text-green-700">
                        {formatPrice(calculatePaidFromInstallments())} pago
                      </span>
                      <span className="text-gray-600 mx-2">de</span>
                      <span className="font-bold">{formatPrice(formData.installments.reduce((s, i) => s + (parseFloat(i.value) || 0), 0))}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Campo de Desconto */}
            {selectedPackage && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="grid grid-cols-3 gap-4 items-end">
                  <div>
                    <Label className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-yellow-600" />
                      Desconto (%)
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.0000000001"
                      value={formData.discount_percentage}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount_percentage: e.target.value }))}
                      placeholder="0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500">Subtotal</Label>
                    <p className="text-lg font-medium text-gray-600 line-through">{formatPrice(calculateSubtotal())}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500">Valor do Desconto</Label>
                    <p className="text-lg font-bold text-yellow-600">- {formatPrice(calculateDiscount())}</p>
                  </div>
                </div>
                {parseFloat(formData.discount_percentage) > 0 && (
                  <p className="text-xs text-yellow-700 mt-2">
                    💰 Desconto de {formData.discount_percentage}% aplicado ao valor total
                  </p>
                )}
              </div>
            )}

            {/* Local de Embarque */}
            {selectedPackage && (
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <Label className="flex items-center gap-2 mb-2">
                  {selectedPackage.category?.includes('aereo') || selectedPackage.category?.includes('internacional') ? (
                    <Plane className="w-4 h-4 text-orange-600" />
                  ) : (
                    <Bus className="w-4 h-4 text-orange-600" />
                  )}
                  Local de Embarque
                </Label>
                {/* Verificar se o pacote tem boarding_points definidos */}
                {selectedPackage.boarding_points && selectedPackage.boarding_points.length > 0 ? (
                  <Select 
                    value={formData.boarding_point} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, boarding_point: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o local de embarque" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedPackage.boarding_points.map((point, idx) => (
                        <SelectItem key={idx} value={typeof point === 'string' ? point : (point.name || point.city || point)}>
                          {typeof point === 'string' ? point : (point.name || point.city || point)} {point.time ? `- ${point.time}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (selectedPackage.category?.includes('aereo') || selectedPackage.category?.includes('internacional')) ? (
                  /* Usar cidades de partida (configuração) para aéreos/internacionais */
                  <Select 
                    value={formData.boarding_point} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, boarding_point: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a cidade de embarque" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDepartureCities.map((city, idx) => (
                        <SelectItem key={idx} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (selectedPackage.category?.includes('rodoviario') || selectedPackage.category?.includes('1-dia') || selectedPackage.category?.includes('bate-volta')) ? (
                  /* Usar pontos de embarque (configuração) para rodoviários */
                  <Select 
                    value={formData.boarding_point} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, boarding_point: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o ponto de embarque" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBoardingPoints.map((point, idx) => (
                        <SelectItem key={idx} value={point}>
                          {point}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  /* Fallback: Input de texto livre */
                  <Input
                    value={formData.boarding_point}
                    onChange={(e) => setFormData(prev => ({ ...prev, boarding_point: e.target.value }))}
                    placeholder="Digite o local de embarque"
                  />
                )}
              </div>
            )}

            {/* Payment Info Summary - Status Automático */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
              <div>
                <Label>Valor Total (calculado)</Label>
                <div className="text-xl font-bold text-[#003366]">{formatPrice(calculateTotal())}</div>
              </div>
              <div>
                <Label>Valor Pago (parcelas)</Label>
                <div className="text-xl font-bold text-green-600">{formatPrice(calculatePaidFromInstallments())}</div>
              </div>
              <div>
                <Label>Valor Restante</Label>
                <div className={`text-xl font-bold ${(calculateTotal() - calculatePaidFromInstallments()) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                  {formatPrice(Math.max(0, calculateTotal() - calculatePaidFromInstallments()))}
                </div>
              </div>
              <div>
                <Label>Status (automático)</Label>
                <div className={`text-xl font-bold ${
                  formData.status === 'cancelado' ? 'text-gray-600' :
                  calculateAutoStatus().includes('Total') ? 'text-green-600' :
                  calculateAutoStatus().includes('Atrasado') ? 'text-red-600' : 'text-yellow-600'
                }`}>
                  {formData.status === 'cancelado' ? '❌ Cancelado' : calculateAutoStatus()}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Observações</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Anotações sobre esta reserva..."
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#003366] hover:bg-[#002244]">
                <Save className="w-4 h-4 mr-2" />
                {editingReservation ? 'Atualizar' : 'Criar Reserva'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManualReservations;
